<script setup lang="ts">
import { useAlertNotifications, useSiteAlerts } from "@/api/alert-query";
import { useSite } from "@/api/site-query";
import { createNavigation } from "@/router";
import { TimeRange } from "@/stores";
import { formatTimeRange, generateTimeRangeOffset, RangeMode } from "@/utils/time-range";
import {
	mdiBellCog,
	mdiChevronDown,
	mdiChevronUp,
	mdiListBoxOutline,
	mdiReload,
	mdiTableArrowDown,
} from "@mdi/js";
import { ActiveAlertDataNotification, SeverityLevel } from "@soft-db/monitoring-api-client";
import { Button, Icon, LinkButton, makeColumnHelper, Tooltip, useScreenSize } from "@soft-db/ui";
import { DateTime } from "luxon";
import AppDataTable from "../common/AppDataTable.vue";
import AppTimeRangeMenu from "../common/AppTimeRangeMenu.vue";
import DataWithSaturation from "../common/DataWithSaturation.vue";
import AlertSeverityChip from "./AlertSeverityChip";
import { useAlertNotificationExporter } from "./composable/alert-notification-exporter";

type NotificationRowData = {
	alert: string;
	startTime: string;
	endTime: string;
	timezone: string;
	label: string;
	value: string;
	saturated: boolean;
	limit: string;
	severity: SeverityLevel;
	data: (Omit<NotificationRowData, "data"> & { data: [] })[];
};

const SeverityWeight = {
	error: 2,
	warning: 1,
	none: 0,
} as const satisfies Record<SeverityLevel, number>;

const props = defineProps({
	id: {
		type: String,
		required: true,
	},
});

const { t } = useI18n();
const { mdOrLower } = useScreenSize();

const timeMode = ref<RangeMode>("last_48h");

const range = shallowRef<TimeRange>({
	start: DateTime.now(),
	end: DateTime.now(),
});

function refreshRange() {
	const now = DateTime.now();

	if (timeMode.value === "custom") {
		return;
	}

	range.value = {
		start: now.minus(generateTimeRangeOffset(timeMode.value)),
		end: now,
	};
}

refreshRange();
watch(timeMode, refreshRange);

const { site } = useSite(computed(() => props.id));
const { alerts } = useSiteAlerts(computed(() => props.id));
const { data: notifications, isLoading } = useAlertNotifications(
	computed(() => props.id),
	computed(() => [range.value.start.toJSDate(), range.value.end.toJSDate()])
);

function dataWeight(data: ActiveAlertDataNotification) {
	if (data.isMain) return Number.MAX_SAFE_INTEGER;

	const severityWeight = SeverityWeight[data.severity] * 100;
	return severityWeight + (data.priority === "high" ? 1 : 0);
}

function sortData(data: ActiveAlertDataNotification[]) {
	return data.sort((a, b) => dataWeight(a) - dataWeight(b)).reverse();
}

const mappedNotifications = computed<NotificationRowData[]>(() => {
	if (!notifications.value) return [];

	return notifications.value
		.filter((notification) => !!notification.data.length)
		.map<NotificationRowData>((notification) => {
			const allData = sortData([...notification.data]);
			const mainData = allData[0];

			// Remove first item from the list
			allData.splice(0, 1);

			return {
				alert: notification.alert,
				startTime: mainData.startTime,
				endTime: mainData.endTime,
				timezone: mainData.timezone,
				label: mainData.label,
				value: mainData.value,
				limit: mainData.trigger,
				severity: mainData.severity,
				saturated: mainData.saturated,
				data: allData.map((data) => ({
					alert: "",
					startTime: "",
					endTime: "",
					timezone: "",
					label: data.label,
					value: data.value,
					limit: data.trigger,
					severity: data.severity,
					saturated: data.saturated,
					data: [],
				})),
			};
		});
});

function getAlertName(id: string) {
	const found = alerts.value?.find((alert) => alert.id === id);
	return found?.name || "-";
}

const exporter = useAlertNotificationExporter();

function exportToCSV() {
	if (site.value == null || notifications.value == null || notifications.value?.length === 0) {
		return;
	}
	exporter.toCSV(site.value, notifications.value);
}

const { Column } = makeColumnHelper<NotificationRowData>();
</script>

<template>
	<AppDataTable
		layout="simple"
		:data="mappedNotifications"
		:loading="isLoading"
		:pagination="{ pageIndex: 0, pageSize: 10 }"
		:options="{
			getSubRows: (row) => row.data,
		}"
	>
		<template #title>
			<div class="flex items-center gap-2">
				<h2>{{ t("alert-settings.alertsHistory") }}</h2>
				<Icon class="size-6 shrink-0" :d="mdiListBoxOutline" />
			</div>
		</template>
		<template #action>
			<div>
				<Tooltip :disabled="timeMode !== 'custom'" :tooltip="t('alert-settings.refreshDisabled')">
					<span>
						<Button
							class="mr-2"
							variant="outline"
							:disabled="timeMode === 'custom'"
							@click="refreshRange"
						>
							<Icon :d="mdiReload" />
							<span>{{ t("common.refresh") }}</span>
						</Button>
					</span>
				</Tooltip>

				<AppTimeRangeMenu
					class="rounded-r-none border-r-0"
					:class="mdOrLower ? 'size-8' : ''"
					:range="range"
					:mode="timeMode"
					:availableModes="{ last_24h: true, last_48h: true, last_7d: true }"
					@update:range="(r) => (range = r)"
					@update:mode="(m) => (timeMode = m)"
				/>
				<Tooltip :tooltip="t('common.download')">
					<span>
						<Button
							class="rounded-l-none"
							variant="outline"
							shape="square"
							:disabled="!notifications || notifications.length === 0"
							@click="exportToCSV"
						>
							<Icon :d="mdiTableArrowDown" />
						</Button>
					</span>
				</Tooltip>
			</div>
		</template>
		<template #columns>
			<Column v-slot="{ row: { original: notif } }" field="label" :header="t('common.name')">
				<div class="flex w-fit flex-col">
					<h5>{{ notif.label }}</h5>
					<span v-if="notif.alert" class="text-xs">{{ getAlertName(notif.alert) }}</span>
				</div>
			</Column>

			<Column v-slot="{ row: { original: notif } }" field="startTime" :header="t('common.date')">
				<div v-if="notif.startTime && notif.endTime" class="flex w-fit flex-col">
					<span>
						{{ formatTimeRange(notif.startTime, notif.endTime, { timezone: notif.timezone }) }}
					</span>
				</div>
			</Column>

			<Column v-slot="{ row: { original: notif } }" field="value" :header="t('common.value')">
				<DataWithSaturation :data="notif.value" :saturated="notif.saturated" />
			</Column>

			<Column
				v-slot="{ row: { original: notif } }"
				field="severity"
				:header="t('alert-settings.severity')"
			>
				<AlertSeverityChip :severity="notif.severity" />
			</Column>

			<Column v-slot="{ row }" shrink>
				<Button
					v-if="row.getCanExpand()"
					variant="ghost"
					size="xs"
					@click="
						(e) => {
							e.stopPropagation();
							row.toggleExpanded();
						}
					"
				>
					<Icon :d="row.getIsExpanded() ? mdiChevronUp : mdiChevronDown" />
				</Button>
			</Column>

			<Column v-slot="{ row: { original: notif } }" shrink>
				<LinkButton
					v-if="
						notif.alert &&
						createNavigation({
							name: 'sites-id-alerts-alertid',
							params: { id, alertid: notif.alert },
						})
					"
					shape="square"
					variant="soft"
					color="primary"
					:to="
						createNavigation({
							name: 'sites-id-alerts-alertid',
							params: { id, alertid: notif.alert },
						})!
					"
				>
					<Icon :d="mdiBellCog" />
				</LinkButton>
			</Column>
		</template>
	</AppDataTable>
</template>
